'use client';

/**
 * AmbientBackground — sostituto leggero di MaritimeBackground.
 *
 * Layout statico (gradient + glow + grid) + bolle viewport-aware:
 *  - 6 bolle STATICHE (posizione fissa, zero animation)
 *  - 3 bolle ANIMATE solo quando section è visibile (IntersectionObserver)
 *
 * Animation pause quando fuori viewport → no compositing overhead su section
 * non visibili. Hidden mobile + motion-reduce.
 */

import React, { useEffect, useRef, useState } from 'react';

interface AmbientBackgroundProps {
  variant?: 'light' | 'dark';
  position?: 'fixed' | 'absolute';
}

const STATIC_BUBBLES = [
  { left: '5%', top: '15%', size: 10 },
  { left: '14%', top: '62%', size: 14 },
  { left: '22%', top: '32%', size: 7 },
  { left: '36%', top: '78%', size: 18 },
  { left: '44%', top: '20%', size: 11 },
  { left: '58%', top: '48%', size: 13 },
  { left: '68%', top: '72%', size: 9 },
  { left: '76%', top: '28%', size: 17 },
  { left: '85%', top: '58%', size: 12 },
  { left: '94%', top: '38%', size: 8 },
];

const ANIMATED_BUBBLES = [
  { left: '10%', size: 20, duration: 14, delay: 0 },
  { left: '28%', size: 16, duration: 18, delay: 2 },
  { left: '52%', size: 24, duration: 16, delay: 4 },
  { left: '72%', size: 18, duration: 15, delay: 6 },
  { left: '88%', size: 14, duration: 17, delay: 8 },
];

const AmbientBackground: React.FC<AmbientBackgroundProps> = ({ variant = 'light', position = 'absolute' }) => {
  const isDark = variant === 'dark';
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          setIsVisible(entry.isIntersecting);
        }
      },
      { threshold: 0.05, rootMargin: '100px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const bubbleBg = isDark
    ? 'radial-gradient(circle at 30% 30%, rgba(56,189,248,0.22), rgba(56,189,248,0.06))'
    : 'radial-gradient(circle at 30% 30%, rgba(14,165,233,0.32), rgba(14,165,233,0.10))';
  const bubbleBorder = isDark
    ? '1px solid rgba(56,189,248,0.10)'
    : '1px solid rgba(14,165,233,0.16)';

  return (
    <div
      ref={ref}
      className={`${position} inset-0 pointer-events-none overflow-hidden z-0`}
      aria-hidden
    >
      {/* Base gradient — diagonale azzurro */}
      <div className={`absolute inset-0 ${
        isDark
          ? 'bg-gradient-to-br from-[#0a1628] via-[#0d2847] to-[#081a30]'
          : 'bg-gradient-to-br from-[#dbeafe] via-[#bae6fd] to-[#e0f2fe]'
      }`} />

      {/* Radial glow top-left (static) */}
      <div className={`absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full blur-3xl ${
        isDark
          ? 'bg-[radial-gradient(ellipse,rgba(56,189,248,0.08),transparent_70%)]'
          : 'bg-[radial-gradient(ellipse,rgba(56,189,248,0.28),transparent_70%)]'
      }`} />

      {/* Radial glow bottom-right (static) */}
      <div className={`absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full blur-3xl ${
        isDark
          ? 'bg-[radial-gradient(ellipse,rgba(14,165,233,0.07),transparent_70%)]'
          : 'bg-[radial-gradient(ellipse,rgba(14,165,233,0.22),transparent_70%)]'
      }`} />

      {/* Subtle dotted grid */}
      <div className="absolute inset-0"
        style={{
          backgroundImage: `radial-gradient(circle, ${isDark ? '#38bdf8' : '#0284c7'} 1px, transparent 1px)`,
          backgroundSize: '48px 48px',
          opacity: isDark ? 0.012 : 0.035,
        }}
      />

      {/* Bolle statiche — sempre visibili, zero animation */}
      <div className="absolute inset-0 hidden md:block">
        {STATIC_BUBBLES.map((b, i) => (
          <div
            key={`static-${i}`}
            className="absolute rounded-full"
            style={{
              left: b.left,
              top: b.top,
              width: b.size,
              height: b.size,
              background: bubbleBg,
              border: bubbleBorder,
              opacity: 0.7,
            }}
          />
        ))}
      </div>

      {/* Bolle animate — solo quando section in viewport */}
      <div className="absolute inset-0 hidden md:block motion-reduce:hidden">
        {ANIMATED_BUBBLES.map((b, i) => (
          <div
            key={`anim-${i}`}
            className="absolute rounded-full"
            style={{
              left: b.left,
              width: b.size,
              height: b.size,
              bottom: '-20px',
              background: bubbleBg,
              border: bubbleBorder,
              animation: `bubble-rise ${b.duration}s ease-in-out infinite ${b.delay}s`,
              animationPlayState: isVisible ? 'running' : 'paused',
              opacity: isVisible ? 1 : 0,
              willChange: isVisible ? 'transform, opacity' : 'auto',
              transform: 'translateZ(0)',
              transition: 'opacity 600ms ease-out',
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes bubble-rise {
          0%   { transform: translateY(0) translateX(0) scale(0.6); opacity: 0; }
          8%   { opacity: 0.9; }
          50%  { transform: translateY(-50vh) translateX(-10px) scale(1.05); }
          100% { transform: translateY(-110vh) translateX(8px) scale(0.7); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default AmbientBackground;
